// Zone and pin type definitions loaded from JSON resource files
// Labels are i18n keys resolved at display time via t(labelKey)

import type { ZoneType, PinType, ZoneTypeDefinition, PinTypeDefinition } from '@/types/zone'
import { t } from '@/i18n'
import zonesData from '@/data/zones.json'
import pinsData from '@/data/pins.json'

/** Parse hex color string to number (e.g. "#FFD700" → 0xFFD700) */
function parseColor(hex: string): number {
  return parseInt(hex.slice(1), 16)
}

/** Build zone definitions from JSON data */
function buildZoneDefs(): ZoneTypeDefinition[] {
  return zonesData.map(z => ({
    type: z.type as ZoneType,
    color: parseColor(z.color),
    defaultHeight: z.defaultHeight,
    label: t(z.labelKey, z.type),
    labelKey: z.labelKey,
    category: z.category as ZoneTypeDefinition['category'],
  }))
}

/** Build pin definitions from JSON data */
function buildPinDefs(): PinTypeDefinition[] {
  return pinsData.map(p => ({
    type: p.type as PinType,
    color: parseColor(p.color),
    label: t(p.labelKey, p.type),
    labelKey: p.labelKey,
    icon: p.icon,
  }))
}

// Build definitions once at module load
const allZones = buildZoneDefs()
const allPins = buildPinDefs()

/**
 * All zone types combined
 */
export const ALL_ZONE_TYPES: ZoneTypeDefinition[] = allZones

/**
 * Functional zones
 */
export const FUNCTIONAL_ZONES: ZoneTypeDefinition[] = allZones.filter(z => z.category === 'functional')

/**
 * Planting zones
 */
export const PLANTING_ZONES: ZoneTypeDefinition[] = allZones.filter(z => z.category === 'planting')

/**
 * Infrastructure zones
 */
export const INFRASTRUCTURE_ZONES: ZoneTypeDefinition[] = allZones.filter(z => z.category === 'infrastructure')

/**
 * Context pin types
 */
export const PIN_TYPES: PinTypeDefinition[] = allPins

/**
 * Zone type lookup map
 */
export const ZONE_TYPE_MAP: Map<ZoneType, ZoneTypeDefinition> = new Map(
  allZones.map(def => [def.type, def])
)

/**
 * Pin type lookup map
 */
export const PIN_TYPE_MAP: Map<PinType, PinTypeDefinition> = new Map(
  allPins.map(def => [def.type, def])
)

/**
 * Get zone type definition by type
 */
export function getZoneTypeDef(type: ZoneType): ZoneTypeDefinition {
  const def = ZONE_TYPE_MAP.get(type)
  if (!def) {
    throw new Error(`Unknown zone type: ${type}`)
  }
  return def
}

/**
 * Get pin type definition by type
 */
export function getPinTypeDef(type: PinType): PinTypeDefinition {
  const def = PIN_TYPE_MAP.get(type)
  if (!def) {
    throw new Error(`Unknown pin type: ${type}`)
  }
  return def
}

/**
 * Default zone opacity for transparent prisms
 */
export const ZONE_OPACITY = 0.3

/**
 * Zone outline thickness in meters
 */
export const ZONE_OUTLINE_WIDTH = 0.05

/**
 * Pin cylinder radius in meters
 */
export const PIN_RADIUS = 0.15

/**
 * Pin height in meters
 */
export const PIN_HEIGHT = 0.5

/**
 * Pin label scale
 */
export const PIN_LABEL_SCALE = 0.5
